import React, { useEffect, useState } from "react";
import { Row, Table } from "reactstrap";
import { useTranslation } from 'react-i18next';
import { english } from "flatpickr/dist/l10n/default.js";
import { MandarinTraditional } from "flatpickr/dist/l10n/zh-tw.js";
import { SummaryDate, SummaryFilter } from "../../Components/Common/SVG";
import { useDispatch, useSelector } from "react-redux";
import Flatpickr from "react-flatpickr";
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { REPORT_PAGE_SIZE } from "../../common/constants";
import Select from "react-select";
import colors from "../../assets/scss/config/saas/_colors.scss";
import { getAllCompanies, getConnectionLogByDeviceID, getDeviceIDByCompanyID } from "../../slices/developer/thunk";
import chroma from "chroma-js";
import { setConnectionLogDateRange, setConnectionLogDeviceID, setConnectionLogPage, setConnectionLogSelectedCompanyID, setIsConnectionLogLoading } from "../../slices/developer/reducer";
import { getDisplayDatetime } from "../../helpers/display_help";

export const colourStylesDeviceFilter = (selectColor) => {
    return {
        menu: (styles) => ({
            ...styles,
            backgroundColor: colors.white
        }),
        menuList: (styles) => ({
            ...styles,
            backgroundColor: colors.white
        }),
        control: (styles) => ({
            ...styles,
            marginLeft: 6,
            marginRight: 6,
            backgroundColor: colors.white,
            border: '1px solid #e5e7eb',
            boxShadow: 'none',
            '&:hover': {
                border: '1px solid #e5e7eb',
            },
            minWidth: 200,
        }),

        option: (styles, { isFocused, isSelected }) => ({
            ...styles,
            color: colors.textBlack,            // text always black
            backgroundColor: isSelected
                ? chroma(selectColor).alpha(0.15).css()
                : isFocused
                ? chroma(selectColor).alpha(0.10).css()
                : colors.white,
            ':active': {
                backgroundColor: chroma(selectColor).alpha(0.20).css(),
                color: colors.textBlack
            }
        }),

        singleValue: (styles) => ({
            ...styles,
            color: colors.textBlack,
            fontWeight: 500
        }),
    };
};


function ConnectionTable(props) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    document.title = t('PageMain.DocumentTitle');
    const dateRange = useSelector(state => state.Developer.connection.dateRange);
    const selectedCompanyID = useSelector(state => state.Developer.connection.selectedCompanyID);
    const selectedDeviceID = useSelector(state => state.Developer.connection.selectedDeviceID);
    const companyList = useSelector(state => state.Developer.company);
    const deviceList = useSelector(state => state.Developer.device[selectedCompanyID?.value]) || [];
    const data = useSelector(state => state.Developer.connection.connectionLog);
    const page = useSelector(state => state.Developer.connection.pageNumber);
    const isLoading =  useSelector(state => state.Developer.connection.isLoading);
    const lastUpdated = useSelector(state => state.Developer.connection.lastUpdated);
    const totalConnectionLogs = useSelector(state => state.Developer.connection.totalConnectionLogs);

    useEffect(() => {
        let defaultDate = new Date();
        defaultDate.setMonth(defaultDate.getMonth() - 1);
        if (dateRange === null) {
            setDateRange([new Date().getTime(), defaultDate.getTime()]);
        }
    }, [lastUpdated]);

    let calLang = english;
    if (i18n.language === "cn") {
        calLang = MandarinTraditional;
    }

    useEffect(() => {
       dispatch(getAllCompanies());
    }, []);

    useEffect(() => {
        if (!isLoading && selectedDeviceID !== null && dateRange !== null && dateRange.length === 2) {
            dispatch(setIsConnectionLogLoading(true));
            dispatch(getConnectionLogByDeviceID(selectedDeviceID.value, dateRange, page, REPORT_PAGE_SIZE));
        }
    }, [page, dateRange, selectedDeviceID])

    useEffect(() => {
        if (!isLoading && selectedCompanyID !== null && selectedCompanyID !== undefined && selectedCompanyID !== "") {
            dispatch(getDeviceIDByCompanyID(selectedCompanyID.value));
            dispatch(setConnectionLogPage(1));
           }
    }, [selectedCompanyID])

    const setDateRange = (data) => {
        if (data !== undefined && data !== null && data.length === 2) {
            dispatch(setConnectionLogDateRange([new Date(data[0]).getTime(), new Date(data[1]).getTime()]));
        }
    }

    const setSelectedCompanyID = (data) => {
        if (data !== undefined && data !== null) {
            dispatch(setConnectionLogSelectedCompanyID(data));
        }
    }

    const setSelectedDeviceID = (data) => {
        if (data !== undefined && data !== null) {
            dispatch(setConnectionLogDeviceID(data));
        }
    }


    return (
        <Row className="full-width">
            <div className="report-table-spin" style={{display: (isLoading?"inherit":"none")}}>
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
            <div style={{display: (isLoading?"none":"flex")}}>
            <div className="d-flex flex-row report-header">
                <div className="d-flex flex-row report-header-filter flex-wrap">
                    <div className="d-flex flex-row report-filter-item-container">
                        <div className="filter-icon-box d-flex align-items-center justify-content-center">
                            <SummaryFilter scale={1.2} />
                        </div>
                        <Select
                            value={selectedCompanyID}
                            isMulti={false}
                            isClearable={false}
                            placeholder={t('PageMain.Device.FilterCompanyPlaceholder')}
                            onChange={(value) => {
                                console.log("Selected company ID:", value);
                                setSelectedCompanyID(value);
                            }}
                            options={companyList.map((company) => ({ value: company.id, label: company.name_zh_t }))}
                            styles={colourStylesDeviceFilter(colors.blue)}
                        />
                        <Select
                            value={selectedDeviceID}
                            isMulti={false}
                            isClearable={false}
                            placeholder={t('PageMain.Device.FilterDevicePlaceholder')}
                            onChange={(value) => {
                                console.log("Selected device ID:", value);
                                setSelectedDeviceID(value);
                            }}
                            options={deviceList.map((id) => ({ value: id, label: id }))}
                            styles={colourStylesDeviceFilter(colors.blue)}
                        />
                    </div>
                    <div className="d-flex flex-row report-filter-item-container">
                        <div className="filter-icon-box d-flex align-items-center justify-content-center">
                            <SummaryDate scale={1.2} />
                        </div>
                        <div className="filter-date-box">
                            <Flatpickr
                                className="form-control"
                                onChange={(e) => { setDateRange(e) }
                                }
                                value={dateRange || ""}
                                options={{
                                    mode: "range",
                                    dateFormat: "Y-m-d",
                                    locale: {
                                        ...calLang
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            </div>
            <div className="report-table-container" style={{display: (isLoading?"none":"block")}}>
                <div className="report-table">
                    <Table className="table-nowrap mb-0">
                        <thead className="table-light">
                            <tr>
                                <th scope="col"></th>
                                <th scope="col">{t('PageDeveloper.ConnectionTable.ID')}</th>
                                <th scope="col">{t('PageDeveloper.ConnectionTable.StartDatetime')}</th>
                                <th scope="col">{t('PageDeveloper.ConnectionTable.EndDatetime')}</th>
                                <th scope="col">{t('PageDeveloper.ConnectionTable.TimeDuration')}</th>                                
                                <th scope="col">{t('PageDeveloper.ConnectionTable.isReset')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data && data.length > 0 && data.map((item, index) => (
                                <tr key={index}>
                                    <td>{(page - 1) * REPORT_PAGE_SIZE + index + 1}</td>
                                    <td>{item.id}</td>   
                                    <td>{getDisplayDatetime(item.start_ts * 1000)}</td>
                                    <td>{getDisplayDatetime(item.end_ts * 1000)}</td>
                                    <td>{item.time_difference}</td>
                                    <td>{item.isReset ? "Yes" : "No"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
            <div className="report-pagination-container" style={{display: (isLoading?"none":"block")}}>
                <PaginationControl
                    page={parseInt(page)}
                    between={4}
                    total={parseInt(totalConnectionLogs)}
                    limit={REPORT_PAGE_SIZE}
                    changePage={(page) => {
                        dispatch(setConnectionLogPage(page));
                    }}
                    ellipsis={1}
                />
            </div>
        </Row>
    );
}

export default ConnectionTable;